import React, {
  HTMLAttributes,
  forwardRef,
  ComponentPropsWithRef,
  MouseEvent,
} from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import { ReactDefaultProps } from "../../types/react";

import styles from "./ProductImage.scss";

type ImageSlideBaseProps = {
  src: string;
  alt: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  customClass?: string;
  active?: boolean;
};

export type ImageSlideProps = ImageSlideBaseProps &
  Omit<HTMLAttributes<HTMLImageElement>, "src" | "alt">;

const propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  customClass: PropTypes.string,
  onClick: PropTypes.func,
  active: PropTypes.bool,
} as const;

const defaultProps = {
  customClass: undefined,
  onClick: undefined,
  active: false,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  ImageSlideBaseProps,
  typeof defaultProps
> = defaultProps;

const ImageSlideWithRef = (
  props: ImageSlideProps,
  forwardedRef: ComponentPropsWithRef<"img">["ref"]
): JSX.Element => {
  const { src, alt, customClass, active, onClick, ...restProps } = props;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <button
      type="button"
      className={cx(styles.slide, { [styles.clickable]: onClick })}
      onClick={(e) => {
        if (onClick) onClick(e);
      }}
      tabIndex={active && onClick ? 0 : -1}
    >
      <img
        ref={forwardedRef}
        src={src}
        alt={alt}
        className={cx(styles.image, customClass)}
        {...restProps}
      />
    </button>
  );
};

export const ImageSlide = forwardRef(ImageSlideWithRef);

ImageSlide.displayName = "ProductImageImageSlide";

ImageSlide.propTypes = propTypes;
ImageSlide.defaultProps = validatedDefaultProps;

export default ImageSlide;
